import React, { useEffect, useState, useRef } from "react";
import logo_bota from "../../assets/img/logo_bota.png";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { persistor } from "../../redux/configStore";
import { settings, http, ACCESSTOKEN, ACCESSTOKEN_LAZADA } from "../../utils/config";
import { cacheInvalidatePrefix } from "../../utils/cache";
import { fetchOrderCount } from "../../api/orderPing";
import { Badge, Button, Dropdown, Popover, Skeleton, Space, Tooltip } from "antd";
import {
  BellOutlined,
  CloseCircleOutlined,
  DownOutlined,
  GlobalOutlined,
  IdcardOutlined,
  InboxOutlined,
  LogoutOutlined,
  MenuOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  UpOutlined,
  UserOutlined,
  WarningOutlined,
  ShoppingOutlined,
  StockOutlined,
} from "@ant-design/icons";
import useOrderNotifier from "../../hooks/useOrderNotifier";
import { getNotificationType } from "../../constants/notificationTypes";
import { relativeTime } from "../../utils/notificationMapper";
import { useTheme } from "../../contexts/ThemeContext";

const SUMMARY_CACHE_MS = 60_000; // 1 phút

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);
const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

/* ─── Icon string → component map (driven by notificationTypes.js) ─── */
const ICON_MAP = {
  ShoppingCartOutlined: ShoppingCartOutlined,
  CloseCircleOutlined: CloseCircleOutlined,
  WarningOutlined: WarningOutlined,
};

export default function HeaderBota({ isMobile, onMenuClick }) {
  const { userData } = useSelector((state) => state.persistedReducer.Login);
  const idw = userData?.idw;
  const {
    badge,
    items: notifItems,
    groups: notifGroups,
    loading: notifLoading,
    markRead,
    forceRefresh,
    clearAll: clearNotifications,
  } = useOrderNotifier();
  const { isDark, toggleTheme } = useTheme();

  const [open, setOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [summary, setSummary] = useState({ orderCount: null, lowStockCount: null });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const summaryCacheRef = useRef({ ts: 0 });
  const handleOpenChange = (flag) => {
    setOpen(flag);
  };

  // Khi mở dropdown thông báo mà chưa có item → load tóm tắt (đơn chưa xử lý, sắp hết hàng) để panel hữu ích
  useEffect(() => {
    if (!bellOpen || notifItems.length > 0) return;
    if (!idw) return;
    const now = Date.now();
    if (now - summaryCacheRef.current.ts < SUMMARY_CACHE_MS && summaryCacheRef.current.data) {
      setSummary(summaryCacheRef.current.data);
      return;
    }
    setSummaryLoading(true);
    Promise.all([
      fetchOrderCount(idw).then(({ count }) => ({ orderCount: count })),
      http.post("/inventory/low-stock-count", { idw }).then((res) => ({
        lowStockCount: res.data?.data?.count ?? 0,
      })),
    ])
      .then(([a, b]) => ({ orderCount: a.orderCount, lowStockCount: b.lowStockCount }))
      .then((data) => {
        setSummary(data);
        summaryCacheRef.current = { ts: Date.now(), data };
      })
      .catch(() => setSummary({ orderCount: null, lowStockCount: null }))
      .finally(() => setSummaryLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bellOpen, notifItems.length]);
  // user_token is HttpOnly — userData from Redux (populated by PrivativeRouterLogin)
  const isLoggedIn = !!userData;
  const logOut = () => {
    clearNotifications();
    cacheInvalidatePrefix("products_");
    persistor.pause();
    persistor.flush()
      .then(() => http.get("/logout").catch(() => {}))
      .then(() => {
        settings.delete_cookie("user_idw");
        settings.delete_cookie("access_tokenTiki");
        settings.delete_cookie("refresh_tokenTiki");
        settings.delete_cookie("access_token");
        settings.delete_cookie("refresh_token");
        settings.delete_cookie("shopId");
        settings.delete_cookie("token");
        settings.delete_cookie("sendo_token");
        settings.delete_cookie("vietel_post_token");
        settings.delete_cookie("access_token_vnp");
        settings.delete_cookie("customer_code");
        settings.delete_cookie("accessTokenAhamove");
        settings.delete_cookie("tokenGhn");
        settings.delete_cookie(ACCESSTOKEN);
        settings.delete_cookie("accessTokenGrap");
        settings.delete_cookie("access_tokenTikTok");
        settings.delete_cookie("refresh_tokenTikTok");
        settings.delete_cookie("shop_cipherTikTok");
        settings.delete_cookie(ACCESSTOKEN_LAZADA);
        settings.delete_cookie("refresh_token_lazada");
        settings.delete_cookie("idw_lazada");
        settings.clearAllClientCookies();
        localStorage.removeItem("integration_tiki_connected_at");
        window.location.href = process.env.REACT_APP_LOGOUT_REDIRECT_URL || window.location.origin + "/login";
        return persistor.purge();
      });
  };
  const avatarLetter = (userData?.name?.[0] || userData?.email?.[0] || "U").toUpperCase();
  return (
    <>
      <div className="header_left">
        {isMobile && (
          <Button
            type="text"
            icon={<MenuOutlined style={{ fontSize: 20 }} />}
            onClick={onMenuClick}
            className="header-menu-btn"
            aria-label="Mở menu"
          />
        )}
        <NavLink to="/" className="header-logo">
          <img src={logo_bota} alt="logo" />
        </NavLink>
      </div>
      <div className="header_right">
        <div
          className="header_right-inner"
          style={{ display: "flex", alignItems: "center" }}
        >
          <Space size={8}>
            {isLoggedIn ? (
              <>
                <Dropdown
                  trigger={["click"]}
                  onOpenChange={handleOpenChange}
                  open={open}
                  dropdownRender={() => (
                    <div className="hud-panel">
                      {/* ── User info header ── */}
                      <div className="hud-header">
                        <div className="hud-avatar">{avatarLetter}</div>
                        <div className="hud-info">
                          <div className="hud-name">{userData?.name || userData?.email || "Tài khoản"}</div>
                          <div className="hud-email">{userData?.email || "—"}</div>
                        </div>
                      </div>
                      <div className="hud-meta">
                        <span className="hud-meta-item">
                          <GlobalOutlined /> {userData?.domain_web || "—"}
                        </span>
                        <span className="hud-meta-item">
                          <IdcardOutlined /> {userData?.idw || "—"}
                        </span>
                      </div>
                      <div className="hud-divider" />
                      {/* ── Actions ── */}
                      <NavLink to="/profile" className="hud-action" onClick={() => setOpen(false)}>
                        <UserOutlined /> Thông tin cá nhân
                      </NavLink>
                      <button type="button" className="hud-action hud-action--danger" onClick={logOut}>
                        <LogoutOutlined /> Đăng xuất
                      </button>
                    </div>
                  )}
                >
                  <button
                    type="button"
                    className="header-user-trigger"
                    aria-haspopup="menu"
                    aria-expanded={open}
                  >
                    <span className="header-user-text" title={userData?.email || userData?.name}>
                      {userData?.name || userData?.email || "Tài khoản"}
                    </span>
                    <span className="header-user-chevron" aria-hidden>
                      {open ? <UpOutlined /> : <DownOutlined />}
                    </span>
                  </button>
                </Dropdown>
                <Tooltip title={isDark ? "Chế độ sáng" : "Chế độ tối"}>
                  <Button
                    type="text"
                    className="header-icon-btn"
                    onClick={toggleTheme}
                    aria-label="Chuyển theme"
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                  >
                    {isDark ? <SunIcon /> : <MoonIcon />}
                  </Button>
                </Tooltip>
                <Popover
                  trigger="click"
                  open={bellOpen}
                  onOpenChange={(v) => {
                    setBellOpen(v);
                    if (v && badge > 0) markRead();
                  }}
                  placement="bottomRight"
                  overlayStyle={{ width: 320 }}
                  overlayInnerStyle={{
                    borderRadius: 14,
                    padding: 0,
                    boxShadow:
                      "0 6px 16px 0 rgba(0,0,0,.08), 0 3px 6px -4px rgba(0,0,0,.12)",
                  }}
                  overlayClassName="header-notification-popover"
                  content={
                    <div className="header-notification-panel" style={{ minWidth: 300 }}>
                      {/* ── Header ── */}
                      <div
                        className="header-notification-panel__header"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px 16px 8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 15 }}>
                          Thông báo
                        </span>
                        <Space size={8}>
                          <Tooltip title="Cập nhật">
                            <Button
                              type="text"
                              size="small"
                              icon={<ReloadOutlined spin={notifLoading} />}
                              onClick={forceRefresh}
                              disabled={notifLoading}
                            />
                          </Tooltip>
                          {badge > 0 && (
                            <Button
                              type="link"
                              size="small"
                              onClick={markRead}
                              style={{ padding: 0 }}
                            >
                              Đã đọc
                            </Button>
                          )}
                        </Space>
                      </div>

                      {/* ── Body: timeline grouped by date ── */}
                      <div
                        className="header-notification-panel__body"
                        style={{
                          maxHeight: 360,
                          overflowY: "auto",
                          padding: "4px 0",
                        }}
                      >
                        {/* Skeleton loading */}
                        {notifLoading && notifItems.length === 0 ? (
                          <div style={{ padding: "12px 16px" }}>
                            {[1, 2, 3].map((k) => (
                              <div key={k} style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                                <Skeleton.Avatar active size={28} shape="circle" />
                                <div style={{ flex: 1 }}>
                                  <Skeleton.Input active size="small" style={{ width: "70%", height: 14, marginBottom: 6 }} />
                                  <Skeleton.Input active size="small" style={{ width: "40%", height: 12 }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : notifItems.length === 0 ? (
                          /* Empty state: hiển thị tóm tắt hữu ích (đơn chưa xử lý, sắp hết hàng) */
                          <div className="header-notification-panel__empty" style={{ padding: "16px", minWidth: 280 }}>
                            {summaryLoading ? (
                              <Skeleton active paragraph={{ rows: 2 }} />
                            ) : (
                              <>
                                <div className="header-notification-panel__empty-text" style={{ textAlign: "center", marginBottom: 12 }}>
                                  <InboxOutlined style={{ fontSize: 32, color: "#d9d9d9" }} />
                                  <div style={{ color: "#999", marginTop: 6, fontSize: 13 }}>
                                    Chưa có thông báo mới
                                  </div>
                                </div>
                                {(summary.orderCount != null || summary.lowStockCount != null) && (
                                  <div className="header-notification-panel__summary" style={{ borderTop: "1px solid #f0f0f0", paddingTop: 12 }}>
                                    <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
                                      Tóm tắt nhanh
                                    </div>
                                    {summary.orderCount != null && (
                                      <NavLink
                                        to="/order"
                                        className="header-notification-panel__link"
                                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", color: "#1890ff", fontSize: 13 }}
                                      >
                                        <ShoppingOutlined />
                                        <span>
                                          {summary.orderCount === 0
                                            ? "Không có đơn mới"
                                            : `${summary.orderCount} đơn hàng chưa xử lý`}
                                        </span>
                                      </NavLink>
                                    )}
                                    {summary.lowStockCount != null && (
                                      <NavLink
                                        to="/inventory"
                                        className="header-notification-panel__link"
                                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", color: "#1890ff", fontSize: 13 }}
                                      >
                                        <StockOutlined />
                                        <span>
                                          {summary.lowStockCount === 0
                                            ? "Không có sản phẩm sắp hết hàng"
                                            : `${summary.lowStockCount} sản phẩm sắp hết hàng`}
                                        </span>
                                      </NavLink>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ) : (
                          /* Timeline groups */
                          notifGroups.map((group) => (
                            <div key={group.label} className="header-notification-panel__group">
                              {/* Group label */}
                              <div className="header-notification-panel__group-label"
                                style={{
                                  padding: "8px 16px 4px",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#8c8c8c",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                }}
                              >
                                {group.label}
                              </div>
                              {/* Items in group */}
                              {group.items.map((item) => {
                                const typeDef = getNotificationType(item.type);
                                const IconComp = ICON_MAP[typeDef.icon] || ShoppingCartOutlined;
                                return (
                                  <div
                                    key={item.id}
                                    className="header-notification-panel__item"
                                    style={{
                                      display: "flex",
                                      gap: 10,
                                      padding: "10px 16px",
                                      borderBottom: "1px solid #fafafa",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    {/* Icon dot */}
                                    <div
                                      style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        background: `${typeDef.color}14`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        marginTop: 1,
                                      }}
                                    >
                                      <IconComp style={{ fontSize: 14, color: typeDef.color }} />
                                    </div>
                                    {/* Content */}
                                    <div className="header-notification-panel__item-content" style={{ flex: 1, minWidth: 0 }}>
                                      <div
                                        className="header-notification-panel__item-title"
                                        style={{
                                          fontSize: 13,
                                          fontWeight: 500,
                                          color: "#262626",
                                          lineHeight: "18px",
                                        }}
                                      >
                                        {item.title}
                                      </div>
                                      <div
                                        className="header-notification-panel__item-desc"
                                        style={{
                                          fontSize: 12,
                                          color: "#595959",
                                          lineHeight: "16px",
                                          marginTop: 2,
                                        }}
                                      >
                                        {item.description}
                                      </div>
                                      <div
                                        className="header-notification-panel__item-time"
                                        style={{
                                          fontSize: 11,
                                          color: "#bfbfbf",
                                          marginTop: 4,
                                        }}
                                      >
                                        {relativeTime(item.created_at)}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  }
                >
                  <Badge count={badge} size="small" offset={[-2, 2]}>
                    <Button
                      type="text"
                      className="header-icon-btn"
                      icon={<BellOutlined style={{ fontSize: 18 }} />}
                      aria-label="Thông báo"
                    />
                  </Badge>
                </Popover>
                <Tooltip title="Đăng xuất">
                  <Button
                    type="text"
                    className="header-icon-btn header-logout-btn"
                    icon={<LogoutOutlined />}
                    onClick={logOut}
                    aria-label="Đăng xuất"
                  />
                </Tooltip>
              </>
            ) : (
              <NavLink to={"/login"}>Đăng Nhập</NavLink>
            )}
          </Space>
        </div>
      </div>
    </>
  );
}
